import React, { useState } from 'react';

import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { itemByLanguage } from '@/utils/language';

import InputDialog from '@/components/Dialogs/InputDialog';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { Plan } from '@/_generated/api-clients/xp';
import { userPrefsAtom } from '@/atoms/userPrefs';
import useIsValidPlanName from '@/routes/TrainingPlans/hooks/useIsValidPlanName';

interface RenamePlanProps {
  plan: Plan;
  setShowRenamePlan: (show: boolean) => void;
  showRenamePlan: boolean;
}

const RenamePlan: React.FC<RenamePlanProps> = ({
  plan,
  setShowRenamePlan,
  showRenamePlan,
}) => {
  const { t } = useTranslation();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const isValidPlanName = useIsValidPlanName({
    onInvalidName: () => setShowInvalidNameError(true),
  });
  const { updateName } = useUpdatePlan();

  const onRenamePlan = (newName: string, plan: Plan) => {
    const trimmedName = newName.trim();
    if (!isValidPlanName(trimmedName, plan.ownerId)) {
      return;
    }

    updateName(plan, { ...plan.name, [userLanguage]: trimmedName })
      .then(() => {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {`${trimmedName} ${t('TrainingPlans.toastMessage.renamed')}`}
          </ToastMessageBlock>
        ));
      })
      .catch(() => {
        // Hook already reported to Datadog
      });
  };

  return (
    <>
      <InputDialog
        headerText={t('Generic.giveNewName', { object: t('Generic.plan') })}
        isOpen={showRenamePlan}
        onClose={() => setShowRenamePlan(false)}
        primaryButtonHandler={inputValue => {
          onRenamePlan(inputValue, plan);
          setShowRenamePlan(false);
        }}
        primaryButtonText={t('Button.update')}
        primaryButtonVariant="filled"
        secondaryButtonHandler={() => setShowRenamePlan(false)}
        secondaryButtonText={t('Button.cancel')}
        textInputDefaultValue={itemByLanguage(plan.name)}
        textInputPlaceholder={t('TrainingPlans.planNamePlaceholder')}
        keyDownEnter
      />
      <ConfirmationModal
        bodyText={t('InvalidPlanName.paragraphText')}
        headerText={t('InvalidPlanName.errorHeader')}
        isError={true}
        isOpen={showInvalidNameError}
        onClose={() => setShowInvalidNameError(false)}
        primaryButtonHandler={() => setShowInvalidNameError(false)}
        primaryButtonText={t('Button.close')}
      />
    </>
  );
};
export default RenamePlan;
